<template>
  <div class="bandProfileTracks">
    <Modal v-model="display_quick_creation">
      <div class="trackCreateWrap">
        <TrackQuickCreate :band-id="band.id" @new="updateTracks(0, $event)" />
      </div>
    </Modal>
    <ul class="aliveTracks">
      <TransitionGroup v-if="aliveTracks.length" name="cardlist">
        <li v-for="track in aliveTracks" :key="track.id" class="track">
          <TrackDisplay
            :model-value="track"
            :band="band"
            @update:model-value="updateTracks(track.id, $event)"
          />
        </li>
      </TransitionGroup>
      <li v-if="is_owner" key="newtrack" class="track new">
        <div
          class="box"
          @click="display_quick_creation = !display_quick_creation"
        >
          <ActionButton visual="hollowparam">
            <div class="btnText">
              {{ $t('band.profile.slug.tracks01') }}
            </div>
          </ActionButton>
        </div>
      </li>
    </ul>
    <div v-if="archivedTracks.length" class="archivedTracks">
      <button
        type="button"
        class="headWrap"
        @click="display_archived = !display_archived"
      >
        <i
          class="fa-solid fa-chevron-right"
          :class="{ reversed: display_archived }"
        />
        <span class="title">
          {{ $t('band.profile.slug.tracks00') }}
        </span>
      </button>
      <transition name="fade" mode="out-in">
        <transition-group
          v-if="display_archived"
          key="archivedTracks"
          tag="ul"
          name="cardlist"
          class="archivedTracks"
        >
          <li v-for="track in archivedTracks" :key="track.id" class="track">
            <TrackDisplay
              :key="track.id"
              :model-value="track"
              :band="band"
              @update:model-value="updateTracks(track.id, $event)"
            />
          </li>
        </transition-group>
      </transition>
    </div>
  </div>
</template>

<script lang="ts">
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'

import ActionButton from '~/components/shared/ActionButton.vue'
import Modal from '~/components/shared/modal.vue'
import TrackDisplay from '~/components/track/display.vue'
import TrackQuickCreate from '~/components/track/quickCreate.vue'

import { useUserStore } from '~/stores/user'
import { useUserBandSetStore } from '~/stores/userBandSet'

import type { Band } from '~/types/band'
import type { Track } from '~/types/track'
import type { PropType } from 'vue'

export default defineComponent({
  components: {
    Modal,
    TrackDisplay,
    ActionButton,
    TrackQuickCreate,
  },
  props: {
    band: {
      type: Object as PropType<Band>,
      required: true,
    },
    modelValue: {
      type: Array as PropType<Track[]>,
      required: true,
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      display_archived: false,
      display_quick_creation: false,
    }
  },
  computed: {
    ...mapState(useUserStore, { USER_IS_STAFF: 'is_staff' }),
    is_owner() {
      return this.HAS_BAND(this.band.id) !== -1 || this.USER_IS_STAFF
    },
    aliveTracks() {
      return this.modelValue.filter((track) => track.is_archived === false)
    },
    archivedTracks() {
      return this.modelValue.filter((track) => track.is_archived === true)
    },
  },
  methods: {
    ...mapActions(useUserBandSetStore, {
      HAS_BAND: 'GET_BAND_INDEX_FROM_ID',
    }),
    updateTracks(trackId: number, patch?: Track) {
      const copy = [...this.modelValue]
      const toRemoveIndex = copy.findIndex((e) => e.id === trackId)

      this.display_quick_creation = false

      if (toRemoveIndex >= 0 && patch) {
        copy.splice(toRemoveIndex, 1, patch)
        this.$emit('update:modelValue', copy)
      } else {
        this.$emit('update:modelValue', [...copy, patch])
      }
    },
  },
})
</script>

<style scoped lang="scss">
.archivedTracks,
.aliveTracks {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-wrap: wrap;
  overflow: hidden;
  width: 100%;
}

.track {
  width: 100%;
  max-width: 320px;
  position: relative;
  margin-bottom: 64px;
  margin-right: 32px;
}

.headWrap {
  width: 100%;
  margin-bottom: 44px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  cursor: pointer;
}

.title {
  font-size: var(--font-size-title);
  font-weight: 600;
}

.fa-solid {
  @apply tw-ml-[2px] tw-mr-2 tw-flex-shrink-0;

  transform: rotate(0deg);
  transition: transform 0.15s var(--trans-standard);
}

.fa-solid.reversed {
  transform: rotate(90deg);
  transition: transform 0.1s var(--trans-decelerate);
}

.new .box {
  margin-top: 87px;
  cursor: pointer;
  border: 2px dashed var(--color-param);
  border-radius: 4px;
  height: 178px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.trackCreateWrap {
  max-width: 576px;
  padding: 34px 34px 40px 34px;
  max-height: 90vh;
}

@media screen and (max-width: 720px) {
  .trackCreateWrap,
  .track {
    max-width: unset;
  }

  .new .box {
    margin-top: 0px;
    height: 320px;
  }

  .trackCreateWrap {
    max-height: unset;
  }
}
</style>
